// Lógica pura da aba Fornecedores (Financeiro → Cadastros), 01/10/2026.
//
// Fica separada da tela de propósito: o jest do CRA não carrega o antd v6, então
// teste de componente quebra a suíte inteira. Tudo o que decide algo (filtro,
// número, quem fica na junção) mora aqui e tem teste em logica.test.ts.
import {
  FornecedorSlim, buscaFornecedor, digitosFornecedor, filtrarFornecedor,
} from '../../utils/fornecedores';

export type FiltroRapido = 'todos' | 'sem_cnpj' | 'sem_lancamentos' | 'inativos';

export interface FornecedorLinha extends FornecedorSlim {
  ativo: boolean;
  email: string | null;
  telefone: string | null;
  observacoes: string | null;
  lancamentos: number;
  total_pago: number;
  ultimo_lancamento: string | null;
  // Pré-calculados uma vez só: a busca roda a cada tecla sobre ~1.000 linhas.
  _busca: string;
  _digitos: string;
}

/**
 * `numeric` do Postgres chega como STRING no node-pg. Somar ou ordenar sem
 * converter dá "9" depois de "80" e "10050" no lugar de 150. Valor vazio ou
 * ilegível vira 0 — nunca NaN, que quebra o sorter da tabela.
 */
export function paraNumero(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

export function normalizarFornecedor(raw: any): FornecedorLinha {
  const base: FornecedorSlim = {
    id: String(raw?.id ?? ''),
    nome_fantasia: raw?.nome_fantasia ?? '',
    razao_social: raw?.razao_social ?? null,
    cnpj_cpf: raw?.cnpj_cpf ?? null,
  };
  return {
    ...base,
    // Só "false" explícito é inativo: resposta sem o campo não pode esconder
    // todo mundo sob a etiqueta de inativo.
    ativo: raw?.ativo !== false,
    email: raw?.email ?? null,
    telefone: raw?.telefone ?? null,
    observacoes: raw?.observacoes ?? null,
    lancamentos: Math.round(paraNumero(raw?.lancamentos)),
    total_pago: paraNumero(raw?.total_pago),
    ultimo_lancamento: raw?.ultimo_lancamento ? String(raw.ultimo_lancamento) : null,
    _busca: buscaFornecedor(base),
    _digitos: digitosFornecedor(base),
  };
}

/** "Sem CNPJ" = nenhum dígito. Espaço ou máscara solta não contam como CNPJ. */
export function semCnpj(f: FornecedorSlim): boolean {
  return digitosFornecedor(f).length === 0;
}

export function aplicarFiltroRapido(lista: FornecedorLinha[], filtro: FiltroRapido): FornecedorLinha[] {
  switch (filtro) {
    case 'sem_cnpj': return lista.filter(semCnpj);
    case 'sem_lancamentos': return lista.filter(f => f.lancamentos === 0);
    case 'inativos': return lista.filter(f => !f.ativo);
    default: return lista;
  }
}

export function contarFiltros(lista: FornecedorLinha[]): Record<FiltroRapido, number> {
  return {
    todos: lista.length,
    sem_cnpj: lista.filter(semCnpj).length,
    sem_lancamentos: lista.filter(f => f.lancamentos === 0).length,
    inativos: lista.filter(f => !f.ativo).length,
  };
}

/** Filtro rápido + busca. A régua de busca é a de utils/fornecedores (já testada no CI). */
export function filtrarLista(lista: FornecedorLinha[], filtro: FiltroRapido, busca: string): FornecedorLinha[] {
  return aplicarFiltroRapido(lista, filtro).filter(f => filtrarFornecedor(busca, f));
}

/**
 * Quem FICA numa junção: o que tem CNPJ; havendo empate, o que tem mais
 * lançamentos (menos coisa para renomear no banco); ainda empatado, o primeiro
 * da seleção, para o resultado não depender de ordenação instável.
 */
export function escolherMantido(lista: FornecedorLinha[]): string | null {
  if (!lista.length) return null;
  let melhor = lista[0];
  for (const f of lista.slice(1)) {
    const a = semCnpj(f) ? 0 : 1;
    const b = semCnpj(melhor) ? 0 : 1;
    if (a > b || (a === b && f.lancamentos > melhor.lancamentos)) melhor = f;
  }
  return melhor.id;
}

/** Quantos CNPJs DIFERENTES há na seleção — mais de um sugere filiais, não duplicata. */
export function cnpjsDistintos(lista: FornecedorLinha[]): number {
  return new Set(lista.map(digitosFornecedor).filter(Boolean)).size;
}

export function fmtDinheiro(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Frase de erro do servidor (campo `error` ou `erro`), com plano B legível. */
export function mensagemDeErro(e: any, padrao = 'falha de comunicação'): string {
  const d = e?.response?.data;
  let corpo: any = d;
  if (d && typeof d.body === 'string') {
    try { corpo = JSON.parse(d.body); } catch { corpo = d; }
  }
  return String(corpo?.error || corpo?.erro || corpo?.message || e?.message || padrao);
}

// ── Aba "Sugestões para juntar" (01/10/2026) ─────────────────────────────────
// A REGRA de quem parece duplicado mora no servidor (fornecedoresDuplicados.js,
// testada lá). Aqui só se arruma a resposta para a tela: converter o que chega
// como texto, separar os dois níveis e escrever a frase do rodapé.

export type ConfiancaSugestao = 'alta' | 'media';

export interface SugestaoGrupo {
  id: string;
  confianca: ConfiancaSugestao;
  motivo: string;
  motivo_texto: string;
  // Já normalizados: o ModalMesclarFornecedores espera FornecedorLinha, e o
  // total chega como STRING do numeric.
  fornecedores: FornecedorLinha[];
}

export interface NaoSugeridos {
  documentos_diferentes: number;
  ambiguos: number;
}

export interface Sugestoes {
  grupos: SugestaoGrupo[];
  nao_sugeridos: NaoSugeridos;
  total_fornecedores: number;
}

/**
 * Resposta do GET /financeiro/fornecedores/sugestoes → formato da tela.
 * Devolve null se `grupos` não é lista: servidor velho ou resposta torta tem de
 * virar ERRO na tela, nunca "nenhuma sugestão" (falha não pode parecer vazio).
 * Grupo sem 2 cadastros é descartado — não há o que juntar.
 */
export function normalizarSugestoes(raw: any): Sugestoes | null {
  if (!raw || !Array.isArray(raw.grupos)) return null;
  const grupos: SugestaoGrupo[] = [];
  for (const g of raw.grupos) {
    const fornecedores = (Array.isArray(g?.fornecedores) ? g.fornecedores : []).map(normalizarFornecedor);
    if (fornecedores.length < 2) continue;
    grupos.push({
      id: String(g?.id ?? fornecedores.map((f: FornecedorLinha) => f.id).sort().join('+')),
      // Só 'alta' é alta; qualquer outra coisa cai no nível que pede conferência.
      confianca: g?.confianca === 'alta' ? 'alta' : 'media',
      motivo: String(g?.motivo ?? ''),
      motivo_texto: String(g?.motivo_texto ?? 'Os nomes se parecem.'),
      fornecedores,
    });
  }
  return {
    grupos,
    nao_sugeridos: {
      documentos_diferentes: Math.max(0, Math.round(paraNumero(raw?.nao_sugeridos?.documentos_diferentes))),
      ambiguos: Math.max(0, Math.round(paraNumero(raw?.nao_sugeridos?.ambiguos))),
    },
    total_fornecedores: Math.round(paraNumero(raw?.total_fornecedores)),
  };
}

/** Separa os dois níveis mantendo a ordem que o servidor já deu. */
export function separarPorConfianca(grupos: SugestaoGrupo[]): { alta: SugestaoGrupo[]; media: SugestaoGrupo[] } {
  return {
    alta: grupos.filter(g => g.confianca === 'alta'),
    media: grupos.filter(g => g.confianca !== 'alta'),
  };
}

/**
 * Rodapé: o que o servidor DEIXOU DE sugerir de propósito. Sem isso, quem olha
 * a lista acha que o sistema não viu o par de filiais. Só cita o que é > 0;
 * tudo zerado → null (a tela não mostra rodapé nenhum).
 */
export function fraseRodape(n: NaoSugeridos): string | null {
  const partes: string[] = [];
  if (n.documentos_diferentes > 0) {
    const q = n.documentos_diferentes;
    partes.push(`${q} ${q === 1 ? 'par' : 'pares'} com documentos diferentes (provavelmente filiais)`);
  }
  if (n.ambiguos > 0) {
    const q = n.ambiguos;
    partes.push(`${q} ${q === 1 ? 'nome que cabe' : 'nomes que cabem'} em mais de um cadastro`);
  }
  return partes.length ? `Não sugerimos ${partes.join(' e ')}.` : null;
}
